import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { ApiService } from "./api.service";
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProviderCapturesSearchFilter, ProviderCapturesSearchSettings } from "../models/user-settings/provider-captures-search-settings.model";
import { CaptureQueueCursorType } from "../enums/capture-queue-cursor-type.enum";
import { Cursor, CursorBasedPagingMeta } from "../models/paged-results/cursor-based-paging.model";
import { PagedResults } from "../models/paged-results/paged-results.model";
import { CaptureQueueResult } from "../models/capture-lists/capture-queue-result.model";
import { PagedCaptureQueueResults } from "./capture-queue.service";
import { CaptureQueueFilterName } from "../enums/capture-queue-filter-name.enum";
import {
  appendFilterListParams,
  appendFilterFlagParam,
  appendNullableFilterFlagParam,
  buildCursorParams
} from "../lib/url-utils";

export class PagedCaptureResults
  implements PagedResults<CaptureQueueResult, CursorBasedPagingMeta<CaptureQueueCursorType>> {
  captureQueueResults: CaptureQueueResult[] = new Array<CaptureQueueResult>();
  meta: CursorBasedPagingMeta<CaptureQueueCursorType> = new CursorBasedPagingMeta<CaptureQueueCursorType>();

  get records() {
    return this.captureQueueResults;
  }
}

@Injectable()
export class ProviderCapturesService extends ApiService {
  private http = inject(HttpClient);


  getCaptures(
    providerId: number,
    cursor: Cursor<CaptureQueueCursorType> = null,
    settings: ProviderCapturesSearchSettings,
    limit: number = null
  ): Observable<PagedCaptureResults> {
    const filterParams: URLSearchParams = this.buildFilterParams(settings.filters);

    if (limit) {
      filterParams.append('limit', limit.toString());
    }

    let cursorParams: URLSearchParams = null;
    if (cursor) {
      cursorParams = buildCursorParams(cursor.name, cursor.id, cursor.value);
    } else {
      cursorParams = buildCursorParams(settings.cursorType);
    }

    return this.http.get<PagedCaptureQueueResults>(
      `${environment.captureApi.url}/providers/${providerId}/captures?${filterParams.toString()}&${cursorParams.toString()}`
    );
  }

  getCount(
    providerId: number,
    settings: ProviderCapturesSearchSettings
  ): Observable<{ count: number }> {
    const filterParams: URLSearchParams = this.buildFilterParams(settings.filters);

    return this.http.get<{ count: number }>(
      `${environment.captureApi.url}/providers/${providerId}/captures/count?${filterParams.toString()}`
    );
  }

  private buildFilterParams(filters: ProviderCapturesSearchFilter): URLSearchParams {
    const urlSearchParams = new URLSearchParams();
    appendFilterListParams(CaptureQueueFilterName.statuses, filters.statuses, urlSearchParams);
    appendFilterListParams(CaptureQueueFilterName.reasons, filters.reasons, urlSearchParams);
    appendFilterListParams(CaptureQueueFilterName.clientIds, filters.clientIds, urlSearchParams);
    appendFilterListParams(CaptureQueueFilterName.sources, filters.sources, urlSearchParams);
    appendFilterFlagParam(CaptureQueueFilterName.highValue, filters.highValue, urlSearchParams);
    appendFilterFlagParam(CaptureQueueFilterName.activeClientsOnly, filters.activeClientsOnly, urlSearchParams);
    appendNullableFilterFlagParam(CaptureQueueFilterName.manualFaxSent, filters.manualFaxSent, urlSearchParams);
    return urlSearchParams;
  }
}